import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { UserPlus, UserCheck, Loader2 } from 'lucide-react';
import { followAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';

export function FollowButton({ userId, onFollowChange }) {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !userId || userId === user?.user_id) {
      setChecking(false);
      return;
    }
    const checkStatus = async () => {
      try {
        const res = await followAPI.getFollowStatus(userId);
        setIsFollowing(res.data.is_following);
      } catch (err) {
        // ignore
      } finally {
        setChecking(false);
      }
    };
    checkStatus();
  }, [userId, isAuthenticated, user?.user_id]);

  if (!isAuthenticated || userId === user?.user_id || checking) return null;

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (isFollowing) {
        await followAPI.unfollow(userId);
        setIsFollowing(false);
        toast.success('Unfollowed');
      } else {
        await followAPI.follow(userId);
        setIsFollowing(true);
        toast.success('Following!');
      }
      onFollowChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleToggle}
      disabled={loading}
      variant={isFollowing ? 'outline' : 'default'}
      className={isFollowing
        ? 'border-orange-300 text-orange-600 hover:bg-orange-50'
        : 'btn-boost text-sm'
      }
      data-testid="follow-btn"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <>
          <UserCheck className="w-4 h-4 mr-1" />
          {t('follow.following')}
        </>
      ) : (
        <>
          <UserPlus className="w-4 h-4 mr-1" />
          {t('follow.follow')}
        </>
      )}
    </Button>
  );
}
